<!--
  @project E-School Web
  @author Vothana CHY
  @date 2026-07-28
-->

<script lang="ts" setup>
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { useI18n } from '#imports';
import { PREDEFINED_GRADES, PREDEFINED_SUBJECTS } from '~/constants/courses';

const { t, locale } = useI18n();

// Two-way bindings (v-model compatibility)
const searchQuery = defineModel<string>('searchQuery', { default: '' });
const selectedGrade = defineModel<string>('selectedGrade', { default: 'all' });
const selectedSubject = defineModel<string>('selectedSubject', { default: 'all' });

// Dropdown State
const isGradeDropdownOpen = ref(false);

const toggleGradeDropdown = () => {
  isGradeDropdownOpen.value = !isGradeDropdownOpen.value;
};

const selectGrade = (grade: string) => {
  selectedGrade.value = grade;
  isGradeDropdownOpen.value = false;
};

const tLabel = (kh: string, en: string) => {
  return locale.value === 'kh' ? kh : en;
};

const selectedGradeLabel = computed(() => {
  const grade = PREDEFINED_GRADES.find((g) => g.value === selectedGrade.value);
  if (!grade) return selectedGrade.value;
  return tLabel(grade.labelKh, grade.labelEn);
});

const handleClickOutside = (event: MouseEvent) => {
  const dropdownEl = document.getElementById('grade-dropdown-container');
  if (dropdownEl && !dropdownEl.contains(event.target as Node)) {
    isGradeDropdownOpen.value = false;
  }
};

onMounted(() => {
  document.addEventListener('click', handleClickOutside);
});

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside);
});
</script>

<template>
  <section class="mb-8 flex flex-col gap-4 rounded-2xl border border-gray-100 bg-white p-4 shadow-xs md:p-6">
    <div class="grid grid-cols-1 gap-4 md:grid-cols-3">
      <!-- Search Bar -->
      <div class="relative md:col-span-2">
        <span class="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3.5 text-gray-400">
          <Icon name="lucide:search" class="h-5 w-5" />
        </span>
        <input
          v-model="searchQuery"
          type="text"
          :placeholder="t('courses.searchPlaceholder')"
          class="w-full rounded-xl border border-gray-200 py-3 pr-4 pl-10 text-sm transition focus:border-brand-blue focus:ring-2 focus:ring-brand-blue/10 focus:outline-none"
        />
      </div>

      <!-- Grade Selector Dropdown -->
      <div id="grade-dropdown-container" class="flex flex-col">
        <div class="relative">
          <!-- Trigger Button -->
          <button
            type="button"
            class="flex w-full items-center justify-between rounded-xl border border-gray-200 bg-white py-3 pr-4 pl-10 text-left text-sm transition focus:border-brand-blue focus:ring-2 focus:ring-brand-blue/10 focus:outline-none"
            @click="toggleGradeDropdown"
          >
            <span class="absolute inset-y-0 left-0 flex items-center pl-3.5 text-gray-400">
              <Icon name="lucide:graduation-cap" class="h-5 w-5" />
            </span>
            <span class="text-slate-800">
              {{ selectedGradeLabel }}
            </span>
            <Icon
              name="lucide:chevron-down"
              class="h-4 w-4 text-gray-400 transition-transform duration-200"
              :class="{ 'rotate-180': isGradeDropdownOpen }"
            />
          </button>

          <!-- Options Dropdown Menu -->
          <transition
            enter-active-class="transition duration-200 ease-out"
            enter-from-class="transform -translate-y-2 scale-95 opacity-0"
            enter-to-class="transform translate-y-0 scale-100 opacity-100"
            leave-active-class="transition duration-150 ease-in"
            leave-from-class="transform translate-y-0 scale-100 opacity-100"
            leave-to-class="transform -translate-y-2 scale-95 opacity-0"
          >
            <div
              v-show="isGradeDropdownOpen"
              class="absolute right-0 z-50 mt-2 w-full origin-top-right rounded-xl border border-gray-100 bg-white p-1.5 shadow-lg ring-1 ring-black/5 focus:outline-none"
            >
              <button
                v-for="grade in PREDEFINED_GRADES"
                :key="grade.value"
                type="button"
                class="flex w-full items-center justify-between rounded-lg px-3.5 py-2.5 text-left text-sm text-slate-700 transition hover:bg-slate-50 hover:text-slate-900"
                :class="{ 'bg-slate-50 font-bold text-brand-blue': selectedGrade === grade.value }"
                @click="selectGrade(grade.value)"
              >
                <span>{{ tLabel(grade.labelKh, grade.labelEn) }}</span>
                <Icon v-if="selectedGrade === grade.value" name="lucide:check" class="h-4 w-4 text-brand-blue" />
              </button>
            </div>
          </transition>
        </div>
      </div>
    </div>

    <!-- Subject Chips Filter -->
    <div class="mt-2 flex flex-wrap items-center gap-2 border-t border-gray-50 pt-4">
      <span class="mr-2 text-xs font-bold tracking-wider text-gray-500 uppercase">
        {{ t('courses.subjectsLabel') }}
      </span>
      <button
        v-for="subj in PREDEFINED_SUBJECTS"
        :key="subj.value"
        class="btn-3d-small rounded-full px-4 py-1.5 text-xs font-bold focus-visible:ring-2 focus-visible:ring-brand-blue/20 focus-visible:outline-none"
        :class="
          selectedSubject === subj.value
            ? 'bg-brand-blue text-white'
            : 'bg-slate-100 text-gray-600 hover:bg-slate-200/80 hover:text-slate-800'
        "
        :style="
          selectedSubject === subj.value
            ? '--btn-3d-shadow: rgba(4, 81, 116, 0.35);'
            : '--btn-3d-shadow: rgba(0, 0, 0, 0.08);'
        "
        @click="selectedSubject = subj.value"
      >
        {{ tLabel(subj.labelKh, subj.labelEn) }}
      </button>
    </div>
  </section>
</template>
